import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import dashboardService from '@api/v1/dashboardService'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { AI_REPORT_SUMMARY } from '@config/featureFlags'
import { useAiMode } from '@hooks/useUserSettings'

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : null)

// Build the summary payload from the widgets' ALREADY-FETCHED data in the
// react-query cache (same keys the widgets use → no duplicate network calls).
// Richer than counts alone: chart totals + row counts, KPI descriptions, and a
// few task/appointment titles so the model can name what's due — all still
// grounded (we send only figures the tiles already resolved).
const buildPayload = (qc, name, widgets) => {
  const metrics = []
  const charts = []
  let productionOrigination = null
  let tasksCount = null
  let appointmentsCount = null
  let tasksTitles = []
  let appointmentsTitles = []

  for (const w of widgets || []) {
    if (w.type === 'kpi') {
      const d = qc.getQueryData(['dashboard-formula', w.systemFormulaId])
      if (d?.name) metrics.push({ label: d.name, value: d.value ?? '—', format: d.format || '', description: d.description || '' })
    } else if (w.type === 'chart') {
      const d = qc.getQueryData(['dashboard-chart', w.chart?.chartId])
      if (d?.rows?.length) {
        const nums = d.rows.map((r) => num(r.value)).filter((n) => n != null)
        charts.push({
          name: d.name || w.chart?.name || 'Chart',
          rowCount: d.rows.length,
          total: nums.length ? nums.reduce((a, b) => a + b, 0) : null,
          top: d.rows.slice(0, 5).map((r) => ({ name: r.name, value: r.value })),
        })
      }
    } else if (w.type === 'productionOrigination') {
      productionOrigination = qc.getQueryData(['dashboard-po', 'ytd']) || productionOrigination
    } else if (w.type === 'tasks') {
      const d = qc.getQueryData(['dashboard-activities', 'tasks'])
      if (Array.isArray(d)) { tasksCount = d.length; tasksTitles = d.slice(0, 3).map((i) => i.name).filter(Boolean) }
    } else if (w.type === 'appointments') {
      const d = qc.getQueryData(['dashboard-activities', 'appointments'])
      if (Array.isArray(d)) { appointmentsCount = d.length; appointmentsTitles = d.slice(0, 3).map((i) => i.name).filter(Boolean) }
    }
  }
  return { name, metrics, charts, productionOrigination, tasksCount, appointmentsCount, tasksTitles, appointmentsTitles }
}

/**
 * "✨ Dashboard summary" — a PINNED, first-class feature at the top of every
 * dashboard (never one of the movable tiles). Auto-generates once per dashboard
 * (cached per id for the session; no re-run on tab switches), with a manual
 * Refresh. Self-gates on the per-user AI mode; grounded on the tiles' figures.
 *
 * Mount this with `key={id}` so switching dashboards re-arms the grace period
 * cleanly (the cached summary itself lives in the QueryClient, so a remount
 * still shows a previously-generated summary instantly).
 */
const DashboardAiSummary = ({ id, name, widgets, graceMs = 1200 }) => {
  const qc = useQueryClient()
  const aiMode = useAiMode()
  const [collapsed, setCollapsed] = useState(false)
  // Give the widget queries a beat to populate the cache before we read it
  // (they mount alongside this card). Overridable to 0 in tests.
  const [ready, setReady] = useState(graceMs === 0)

  useEffect(() => {
    if (graceMs === 0 || aiMode === 'off' || !widgets?.length) return
    const t = setTimeout(() => setReady(true), graceMs)
    return () => clearTimeout(t)
  }, [aiMode, widgets?.length, graceMs])

  const enabled = !!AI_REPORT_SUMMARY && aiMode !== 'off' && ready && !!widgets?.length
  const { data: summary = '', isFetching, isError, error, refetch } = useQuery({
    queryKey: ['dashboard-ai-summary', String(id)],
    queryFn: async () => {
      const text = await dashboardService.aiSummary(buildPayload(qc, name, widgets))
      if (!text) throw new Error('No summary was returned.')
      return text
    },
    enabled,
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    retry: false,
  })

  if (!AI_REPORT_SUMMARY || aiMode === 'off') return null

  return (
    <section
      aria-label="AI dashboard summary"
      aria-busy={isFetching}
      className="mb-5 rounded-cb border border-cb-accent/20 bg-gradient-to-br from-cb-light/50 to-white shadow-cb"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-cb-accent/10">
        <h3 className="text-sm font-semibold text-cb-navy flex items-center gap-1.5">
          <span aria-hidden="true">✨</span> Dashboard summary
        </h3>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-cb-navy bg-white border border-gray-200 rounded-lg hover:border-cb-accent/50 hover:bg-cb-light/40 transition-colors disabled:opacity-50"
          >
            <span aria-hidden="true">↻</span> {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Show dashboard summary' : 'Hide dashboard summary'}
            className="px-2 py-1 text-xs font-medium text-gray-500 hover:text-cb-navy"
          >
            {collapsed ? 'Show' : 'Hide'}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-4 py-3">
          {isFetching || (!summary && !isError) ? (
            <LoadingSpinner message="Summarizing this dashboard…" />
          ) : isError ? (
            <div className="text-sm text-red-700">
              {error?.message || 'Summary failed.'}{' '}
              <button type="button" onClick={() => refetch()} className="underline font-medium">Try again</button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700 whitespace-pre-line">{summary}</p>
              <p className="text-[11px] text-gray-500 mt-3">
                AI-generated from this dashboard’s figures — verify against the data before relying on it.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  )
}

export default DashboardAiSummary
